import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, InputNumber, DatePicker, Descriptions, Tag } from 'antd';
import SelectSearchInput from '../../utils/components/selectSearchInput';
import dayjs from 'dayjs';

const DEVISE_GNF = 'GNF';

export const formatGNF = (value: any) =>
  Math.round(Number(value) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const thousands = (value: any) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const unThousands = (value: any) => `${value}`.replace(/\s/g, '');

interface CollectionCreateFormProps {
  visible: boolean;
  onCreate: (values: any) => Promise<void>;
  onCancel: () => void;
  defaultVal?: any;
  clients: any[];
  devises: any[];
  loading?: boolean;
  setClientVisible: () => void;
}

const CollectionCreateForms: React.FC<CollectionCreateFormProps> = ({
  visible,
  onCreate,
  onCancel,
  clients,
  devises,
  loading,
  setClientVisible,
}) => {
  const [form] = Form.useForm();
  const [montant, setMontant] = useState<number>(0);
  const [taux, setTaux] = useState<number>(1);
  const [tauxPayer, setTauxPayer] = useState<number>(1);
  const [devise, setDevise] = useState<string>('');

  useEffect(() => {
    if (!visible) reset();
  }, [visible]);

  const reset = () => {
    form.resetFields();
    setMontant(0);
    setTaux(1);
    setTauxPayer(1);
    setDevise('');
  };

  // Le taux saisi est celui de "palier" unités de la devise (1 pour la plupart,
  // 5000 pour le FCFA par exemple), il faut donc ramener le montant à des
  // tranches de "palier" avant conversion.
  const palier = devises.find((d: any) => d.code === devise)?.palier || 1;
  const convertToGNF = (m: number, t: number) => (m / palier) * t;

  const montantGNF = convertToGNF(montant, taux);
  const montantDu = convertToGNF(montant, tauxPayer);
  const marge = montantGNF - montantDu;

  // Une entrée en GNF n'a pas de conversion: les deux taux valent 1.
  // Pour les autres devises le taux par défaut de 1 est trompeur, on vide le champ
  // pour forcer la saisie du taux du jour.
  const onDeviseChange = (code: string) => {
    form.setFieldsValue({ devise_id: code });
    setDevise(code);
    if (code === DEVISE_GNF) {
      form.setFieldsValue({ taux: 1, taux_payer: 1 });
      setTaux(1);
      setTauxPayer(1);
    } else {
      form.setFieldsValue({ taux: null, taux_payer: null });
      setTaux(0);
      setTauxPayer(0);
    }
  };

  return (
    <Modal
      open={visible}
      width={640}
      title={
        <>
          Nouvelle entrée d'argent
          <Button onClick={() => setClientVisible()} type="dashed" style={{ marginLeft: 8 }}>
            Nouveau Client
          </Button>
        </>
      }
      okText="Enregistrer"
      cancelText="Annuler"
      onCancel={onCancel}
      confirmLoading={loading}
      onOk={() => {
        form
          .validateFields()
          .then(values => {
            onCreate({ ...values, devise_sortie: DEVISE_GNF })
              .then(reset)
              .catch(() => {});
          })
          .catch(info => console.log('Validate Failed:', info));
      }}
    >
      <Form
        autoComplete="on"
        form={form}
        layout="horizontal"
        labelCol={{ span: 7 }}
        name="form_entree"
        initialValues={{
          type_paiement: 'espece',
          motif: 'achat',
          date_operation: dayjs(),
          taux: 1,
          taux_payer: 1,
        }}
      >
        <Form.Item label="Date d'entrée" name="date_operation" rules={[{ required: true }]}>
          <DatePicker format="YYYY-MM-DD" placeholder="date" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="client_id" label="Client" rules={[{ required: true }]}>
          <SelectSearchInput
            onChange={(v) => form.setFieldsValue({ client_id: v })}
            data={clients}
            label="prenom"
            label2="nom"
            label3="telephone"
            valueLabel="id"
            placeholder="Sélectionnez le compte"
          />
        </Form.Item>

        <Form.Item label="Déposant" style={{ marginBottom: 0 }}>
          <Form.Item name="nom_deposant" rules={[{ required: true, message: 'Nom du déposant requis' }]}
            style={{ display: 'inline-block', width: 'calc(60% - 8px)' }}>
            <Input placeholder="nom du déposant" autoComplete="on" />
          </Form.Item>
          <Form.Item name="tel_deposant" style={{ display: 'inline-block', width: 'calc(40% - 8px)', margin: '0 8px' }}>
            <Input placeholder="téléphone" autoComplete="on" />
          </Form.Item>
        </Form.Item>

        <Form.Item name="devise_id" label="Devise" rules={[{ required: true, message: 'Devise requise' }]}>
          <SelectSearchInput
            onChange={onDeviseChange}
            label="code"
            valueLabel="code"
            data={devises}
            placeholder="Devise"
          />
        </Form.Item>

        <Form.Item label="Taux" style={{ marginBottom: 0 }}>
          <Form.Item name="taux" label="Taux du jour" rules={[{ required: true, message: 'Taux du jour requis' }]}
            style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}>
            <InputNumber
              min={0}
              onChange={(v) => setTaux(Number(v) || 0)}
              style={{ width: '100%' }}
              formatter={thousands}
              parser={unThousands as any}
            />
          </Form.Item>
          <Form.Item name="taux_payer" label="Taux à payer" rules={[{ required: true, message: 'Taux à payer requis' }]}
            style={{ display: 'inline-block', width: 'calc(50% - 8px)', margin: '0 8px' }}>
            <InputNumber
              min={0}
              onChange={(v) => setTauxPayer(Number(v) || 0)}
              style={{ width: '100%' }}
              formatter={thousands}
              parser={unThousands as any}
            />
          </Form.Item>
        </Form.Item>

        <Form.Item name="montant" label="Montant reçu" rules={[{ type: 'number', required: true, message: 'Montant requis' }]}>
          <InputNumber
            min={0}
            onChange={(v) => setMontant(Number(v) || 0)}
            placeholder="Montant reçu"
            style={{ width: '100%' }}
            formatter={thousands}
            parser={unThousands as any}
          />
        </Form.Item>

        <Descriptions bordered size="small" column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="Montant en GNF">
            <b>{formatGNF(montantGNF)} GNF</b>
          </Descriptions.Item>
          <Descriptions.Item label="Montant dû">{formatGNF(montantDu)} GNF</Descriptions.Item>
          <Descriptions.Item label="Marge bénéficiaire">
            <Tag color={marge > 0 ? 'green' : marge < 0 ? 'red' : 'default'}>
              {formatGNF(marge)} GNF
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        <Form.Item initialValue="depot" name="type_operation" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="motif" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="type_paiement" noStyle>
          <Input type="hidden" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CollectionCreateForms;
